import React, { useMemo, useState } from "react";
import { SelectChangeEvent, useTheme } from "@mui/material";
import { ClockErrorsLog } from "Types";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import LogsGrid from "./LogsGrid";
import ErrorCard from "./ErrorCard";
import StatInfoCard from "./StatInfoCard";
import DSCollapsibleText from "../../system-design/DSCollapsibleText";

type SeverityFilter = "ALL" | "ERROR" | "WARNING";

interface GroupedError {
  errorSignature: string;
  originalMessage: string;
  count: number;
  deviceCounts: Record<string, number>;
  deviceIds: string[];
  firstOccurrence: string;
  lastOccurrence: string;
  versions: number[];
  severity: "ERROR" | "WARNING";
}

interface ErrorsStatsProps {
  logs: ClockErrorsLog[];
  isLoading?: boolean;
}

const groupErrors = (logs: ClockErrorsLog[]): GroupedError[] => {
  const grouped = logs.reduce<Record<string, GroupedError>>((acc, log) => {
    const signature = log.log;
    const severity = log.severity;
    const groupKey = `${severity}:${signature}`;
    const occurrences = log.occurrences || 1;

    if (!acc[groupKey]) {
      acc[groupKey] = {
        errorSignature: signature,
        originalMessage: log.log,
        count: 0,
        deviceCounts: {},
        deviceIds: [],
        firstOccurrence: log.timestamp,
        lastOccurrence: log.timestamp,
        versions: [],
        severity,
      };
    }

    const group = acc[groupKey];
    group.count += occurrences;
    group.deviceCounts[log.id] =
      (group.deviceCounts[log.id] || 0) + occurrences;

    if (!group.deviceIds.includes(log.id)) group.deviceIds.push(log.id);
    if (!group.versions.includes(log.version)) group.versions.push(log.version);

    const timestamp = new Date(log.timestamp);
    if (timestamp < new Date(group.firstOccurrence))
      group.firstOccurrence = log.timestamp;
    if (timestamp > new Date(group.lastOccurrence)) {
      group.lastOccurrence = log.timestamp;
      group.originalMessage = log.log;
    }

    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => b.count - a.count);
};

const ErrorsStats = ({ logs, isLoading }: ErrorsStatsProps) => {
  const { palette } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedErrorForDialog, setSelectedErrorForDialog] =
    useState<GroupedError | null>(null);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("ALL");

  const groupedErrors = useMemo(() => groupErrors(logs), [logs]);

  const filteredAndSortedErrors = useMemo(() => {
    let filtered = groupedErrors;

    if (severityFilter !== "ALL") {
      filtered = groupedErrors.filter(
        (error) => error.severity === severityFilter,
      );
    }

    return filtered.sort((a, b) => {
      if (severityFilter === "ALL" && a.severity !== b.severity) {
        return a.severity === "ERROR" ? -1 : 1;
      }
      return b.count - a.count;
    });
  }, [groupedErrors, severityFilter]);

  const handleFilterChange = (event: SelectChangeEvent<unknown>) => {
    setSeverityFilter(event.target.value as SeverityFilter);
  };

  const totalErrors = useMemo(
    () =>
      logs
        .filter((log) => log.severity === "ERROR")
        .reduce((sum, log) => sum + (log.occurrences || 1), 0),
    [logs],
  );

  const totalWarnings = useMemo(
    () =>
      logs
        .filter((log) => log.severity === "WARNING")
        .reduce((sum, log) => sum + (log.occurrences || 1), 0),
    [logs],
  );

  const affectedDevices = new Set(logs.map((log) => log.id)).size;

  const handleOpenLogs = (error: GroupedError) => {
    setSelectedErrorForDialog(error);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedErrorForDialog(null);
  };

  const filteredLogsForDialog = useMemo(() => {
    if (!selectedErrorForDialog || !logs) return [];

    return logs.filter((log) => {
      const severity = log.severity === "WARNING" ? "WARNING" : "ERROR";
      return (
        log.log === selectedErrorForDialog.errorSignature &&
        severity === selectedErrorForDialog.severity
      );
    });
  }, [logs, selectedErrorForDialog]);

  return (
    <>
      <DSBox sx={{ p: 3 }}>
        <DSBox
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            mb: 2,
          }}
        >
          <DSTypography variant="h6" sx={{ color: palette.text.primary }}>
            Errors & Warnings {isLoading && "(Loading...)"}
          </DSTypography>
          <DSSelect
            value={severityFilter}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <DSMenuItem value="ALL">All</DSMenuItem>
            <DSMenuItem value="ERROR">Errors</DSMenuItem>
            <DSMenuItem value="WARNING">Warnings</DSMenuItem>
          </DSSelect>
        </DSBox>

        <DSBox sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
          <StatInfoCard
            value={totalErrors}
            label="Total Errors"
            color={palette.error.main}
          />
          <StatInfoCard
            value={totalWarnings}
            label="Total Warnings"
            color="#fd7e14"
          />
          <StatInfoCard
            value={groupedErrors.length}
            label="Unique Types"
            color={palette.info.main}
          />
          <StatInfoCard
            value={affectedDevices}
            label="Devices"
            color={palette.secondary.main}
          />
        </DSBox>

        {filteredAndSortedErrors.length > 0 ? (
          <DSBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 2,
            }}
          >
            {filteredAndSortedErrors.map((error, index) => (
              <ErrorCard
                key={`${error.severity}-${error.errorSignature}`}
                error={error}
                index={index}
                onOpenLogs={handleOpenLogs}
              />
            ))}
          </DSBox>
        ) : (
          <DSBox
            sx={{
              textAlign: "center",
              color: palette.text.secondary,
              p: 5,
              bgcolor: palette.action.hover,
              borderRadius: 2,
            }}
          >
            No errors or warnings to display
          </DSBox>
        )}
      </DSBox>

      <DSDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DSDialogTitle>
          {selectedErrorForDialog?.severity === "WARNING" ? "Warning" : "Error"}{" "}
          Logs - {selectedErrorForDialog?.count || 0} occurrences
          {selectedErrorForDialog && (
            <DSCollapsibleText
              text={selectedErrorForDialog.errorSignature}
              maxLines={3}
            />
          )}
        </DSDialogTitle>
        <DSDialogContent sx={{ p: 0 }}>
          {selectedErrorForDialog && <LogsGrid logs={filteredLogsForDialog} />}
        </DSDialogContent>
        <DSDialogActions>
          <DSButton onClick={handleCloseDialog}>Close</DSButton>
        </DSDialogActions>
      </DSDialog>
    </>
  );
};

export default ErrorsStats;
